// The searches a person keeps: a name, and the criteria `POST /assets/search` takes.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type SavedSearchEntry = components['schemas']['SavedSearchEntry']
export type CreateSavedSearchRequest = components['schemas']['CreateSavedSearchRequest']
export type UpdateSavedSearchRequest = components['schemas']['UpdateSavedSearchRequest']
export type ReorderSavedSearchesRequest = components['schemas']['ReorderSavedSearchesRequest']
export type SavedSearchesArranged = components['schemas']['SavedSearchesArranged']
export type SavedSearchesQuery = NonNullable<operations['saved_searches_list']['parameters']['query']>

/**
 * The caller's saved searches in the order they arranged them, each with what it matches today:
 * `result_count` is computed by this read, over what this caller may read.
 */
export function listSavedSearches(query: SavedSearchesQuery = {}, signal?: AbortSignal): Promise<SavedSearchEntry[]> {
  return api.get<SavedSearchEntry[]>(withQuery('/saved-searches', query), undefined, signal)
}

/**
 * Keep a search, at the end of the caller's list and never run. 409 when they already have one of
 * that name in that scope.
 */
export function createSavedSearch(request: CreateSavedSearchRequest): Promise<SavedSearchEntry> {
  return api.post<SavedSearchEntry>('/saved-searches', request)
}

/** Rename a search, or change what it looks for. Its count is the next listing's to compute. */
export function updateSavedSearch(searchId: string, request: UpdateSavedSearchRequest): Promise<SavedSearchEntry> {
  return api.patch<SavedSearchEntry>(`/saved-searches/${encodeURIComponent(searchId)}`, request)
}

/** Forget a search. */
export function deleteSavedSearch(searchId: string): Promise<void> {
  return api.del<void>(`/saved-searches/${encodeURIComponent(searchId)}`)
}

/** Arrange the caller's searches; a uid that is not theirs is left alone. */
export function reorderSavedSearches(request: ReorderSavedSearchesRequest): Promise<SavedSearchesArranged> {
  return api.put<SavedSearchesArranged>('/saved-searches/order', request)
}
